/**
 * Ops Panel → Admin impersonation ("support mode") handoff.
 *
 * The Ops Panel opens `${ADMIN_URL}?impersonation_code=<code>` (OPS-3.5). The
 * code is single-use and lives 120 s; this module exchanges it for the
 * impersonation token (POST /internal/impersonation/handoff) and signs the tab
 * in as the restaurant. Re-opening the same URL answers 410
 * IMPERSONATION_HANDOFF_INVALID, so a replayed link — from history, a
 * bookmark, a screen share, a referer — no longer re-authenticates.
 *
 * The legacy `?impersonation_token=<jwt>` handoff is still accepted for one
 * release (an Ops Panel deployed before the code handoff sends it). It is the
 * replayable path, which is why the code wins when both are present and a
 * refused code never falls back to it.
 *
 * Order matters and is the point of this module:
 *   1. strip the handoff params from the address bar before anything else, so
 *      no reload, referer or later failure can see or replay them;
 *   2. scope this tab's auth store to sessionStorage (GH-923) before writing
 *      anything, so the operator's own session in localStorage — and every
 *      other open tab watching it — is never touched;
 *   3. only then redeem / write the token and load the profile.
 */
import { create } from "zustand";
import AuthService from "~/services/authentication/auth";
import {
  scopeAuthStoreToImpersonationTab,
  useAuthStore,
} from "~/store/authentication/useAuthStore";
import {
  IMPERSONATION_CODE_URL_PARAM,
  IMPERSONATION_HANDOFF_URL_PARAMS,
  IMPERSONATION_TOKEN_URL_PARAM,
} from "~/lib/impersonation-tab";

/** Backend error code for a code that is unknown, used, expired, or whose session ended. */
export const IMPERSONATION_HANDOFF_INVALID = "IMPERSONATION_HANDOFF_INVALID";

export interface ImpersonationHandoff {
  /** Single-use code (OPS-3.5). Preferred whenever present. */
  code: string | null;
  /** Legacy: the impersonation JWT itself in the URL. */
  token: string | null;
}

/**
 * Why a handoff did not sign the tab in.
 *   - `invalid`: the backend refused the code (410 — used, expired, or the
 *     session already ended; or a malformed code). Retrying cannot help; the
 *     agent has to start a new session from the Ops Panel.
 *   - `unavailable`: the exchange never got an answer about the code (offline,
 *     timeout, 5xx, rate limit). The code may still be good until its 120 s
 *     run out, so it is kept — in memory only — for a retry.
 */
export type ImpersonationHandoffFailure =
  | { kind: "invalid" }
  | { kind: "unavailable"; code: string };

interface ImpersonationHandoffState {
  failure: ImpersonationHandoffFailure | null;
  isRetrying: boolean;
}

/** Not persisted: a failed handoff belongs to this page load only. */
export const useImpersonationHandoffStore = create<ImpersonationHandoffState>()(
  () => ({ failure: null, isRetrying: false }),
);

/**
 * Reads the handoff params and removes them from the address bar in the same
 * step (history.replaceState — no navigation, no reload). Returns null when
 * this page load is not a handoff.
 */
export function takeImpersonationHandoffFromUrl(): ImpersonationHandoff | null {
  if (typeof window === "undefined") return null;
  const search = new URLSearchParams(window.location.search);
  const code = search.get(IMPERSONATION_CODE_URL_PARAM);
  const token = search.get(IMPERSONATION_TOKEN_URL_PARAM);
  if (!code && !token) return null;

  for (const param of IMPERSONATION_HANDOFF_URL_PARAMS) search.delete(param);
  const remaining = search.toString();
  window.history.replaceState(
    {},
    "",
    window.location.pathname +
      (remaining ? `?${remaining}` : "") +
      window.location.hash,
  );
  return { code: code || null, token: token || null };
}

function classifyHandoffFailure(
  error: unknown,
  code: string,
): ImpersonationHandoffFailure {
  const { statusCode, errorCode } = (error ?? {}) as {
    statusCode?: number;
    errorCode?: string;
  };
  if (errorCode === IMPERSONATION_HANDOFF_INVALID || statusCode === 410) {
    return { kind: "invalid" };
  }
  // 400 = a malformed code: the link itself is bad, a retry sends the same
  // bytes. 408/429 are "ask again later", like a 5xx or no answer at all.
  if (
    statusCode !== undefined &&
    statusCode >= 400 &&
    statusCode < 500 &&
    statusCode !== 408 &&
    statusCode !== 429
  ) {
    return { kind: "invalid" };
  }
  return { kind: "unavailable", code };
}

/**
 * Signs this tab in as the restaurant from a handoff taken off the URL.
 * Resolves either way: on a refused or failed code the tab stays signed out
 * and `useImpersonationHandoffStore` carries the reason for root.tsx to show.
 */
export async function startImpersonationFromHandoff(
  handoff: ImpersonationHandoff,
): Promise<void> {
  // GH-923 — repoint this tab's persisted auth store at its own
  // sessionStorage key BEFORE any write, so neither the clear below nor the
  // token write reaches the shared "palate-auth" localStorage key every other
  // open tab's cross-tab sync is watching.
  scopeAuthStoreToImpersonationTab();
  const { clearAuth, setAuth, setProfile } = useAuthStore.getState();
  clearAuth();

  let token = handoff.token;
  if (handoff.code) {
    try {
      const redeemed = await AuthService.redeemImpersonationHandoff(handoff.code);
      token = redeemed.accessToken;
      useImpersonationHandoffStore.setState({ failure: null });
    } catch (error) {
      // Never fall back to a legacy token on the same URL: that is the
      // replayable credential the code exists to retire.
      useImpersonationHandoffStore.setState({
        failure: classifyHandoffFailure(error, handoff.code),
      });
      return;
    }
  }
  if (!token) return;

  setAuth({ token });
  try {
    setProfile(await AuthService.getMe());
  } catch {
    // Owner lookup failed (e.g. org has no OWNER user). Clear so the route
    // guard sends them to /login rather than rendering a half-authenticated
    // state.
    clearAuth();
  }
}

/** Re-attempts a handoff whose exchange never got an answer. No-op otherwise. */
export async function retryImpersonationHandoff(): Promise<void> {
  const { failure, isRetrying } = useImpersonationHandoffStore.getState();
  if (failure?.kind !== "unavailable" || isRetrying) return;
  useImpersonationHandoffStore.setState({ isRetrying: true });
  try {
    await startImpersonationFromHandoff({ code: failure.code, token: null });
  } finally {
    useImpersonationHandoffStore.setState({ isRetrying: false });
  }
}
